import React, { useRef, useState, useEffect } from "react";
import { Form, Input, Button, Card, AutoComplete, Space, Typography, Divider } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Layout from "../../ReusableComponents/Layout";
import postCodeFetch from "../../Redux/APIs/postCodeFetch";
import { useDispatch } from "react-redux";
import putCompany from "../../Redux/APIs/CompanyAPIs/put";
import postCompany from "../../Redux/APIs/CompanyAPIs/post";
import { AddressLookupDetailsAPI } from "../../Redux/APIs/AddressDetailsAPI";
import { Icons } from "../../Utils/icons-utils";
import { debounce } from "../../Utils/debounceUtils";

const { Title, Text } = Typography;

const AddCompany = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const data = JSON.parse(localStorage.getItem("company"));

  const [AddSuggData, setAddSuggData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [enteredText, setEnteredText] = useState("");

  const debouncedGetAddress = debounce(() => {
    setAddSuggData([]);
    dispatch(postCodeFetch(searchQuery, setAddSuggData));
  }, 500);

  useEffect(() => {
    if (enteredText.length > 0) {
      debouncedGetAddress();
    }
  }, [enteredText]);

  const service = JSON.parse(localStorage.getItem("service"));
  const currentServiceID = localStorage.getItem("currentServiceID");
  const currentServiceName = localStorage.getItem("currentServiceName");
  const condition = localStorage.getItem("popUp") === "true";

  const serviceID = condition ? currentServiceID : service?._id;
  const serviceName = condition ? currentServiceName : service?.name;
  const serviceIcon = condition ? Icons[currentServiceName] : service?.image;

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        name: data.name,
        addressline1: data.address.address1,
        addressline2: data.address.address2,
        town: data.address.town,
        county: data.address.county,
        postCode: data.address.postCode,
        longitude: data.address.longitude,
        latitude: data.address.latitude,
      });
    }
  }, []);

  const onFinish = (values) => {
    const payload = {
      service: serviceID,
      name: values.name,
      address1: values.addressline1,
      address2: values.addressline2,
      town: values.town,
      county: values.county,
      postCode: values.postCode,
      longitude: values.longitude,
      latitude: values.latitude,
      required: returnRequiredArray(serviceName),
      image: serviceIcon,
    };

    if (data) {
      dispatch(putCompany({ id: data._id, ...payload }));
    } else {
      dispatch(postCompany(payload, () => navigate("/services")));
    }
  };

  const onAddressSelect = async (value, option) => {
    const address = await AddressLookupDetailsAPI(option.addressId);
    form.setFieldsValue({
      name: address?.line_1 || "",
      addressline1: address?.line_1 || "",
      addressline2: address?.line_2 || "",
      county: address?.county || "",
      town: address?.town_or_city || "",
      latitude: `${address?.latitude}` || "",
      longitude: `${address?.longitude}` || "",
      postCode: address?.postcode || "",
    });
    setSearchQuery(value);
  };

  const returnRequiredArray = (service) => {
    const map = {
      "Council": ["Reference number"],
      "Water": ["Account number", "Final meter reading"],
      "Energy": ["Account number", "Electric meter reading", "Gas meter reading"],
      "Bank": ["Account number", "Sort code"],
      "Broadband": ["Account number"],
      "TV-Subscriptions": ["Account number"],
      "Doctor": ["NHS Number"],
      "Home-Insurance": ["Policy number"],
      "School": ["Pupil Name"],
      "Life-Insurance": ["Policy Number"],
      "Health-Insurance": ["Policy number"],
      "Private-Pension": ["Reference number"],
      "Pet-Insurance": ["Policy number"],
      "Vet": ["Account number"],
      "Loans": ["Reference number"],
      "Premium-Bonds": ["Reference number"],
      "Digital-Subscriptions": ["Account number"],
      "Store-Cards": ["Account number"],
      "University": ["Student ID"],
      "Gym & Health Club": ["Membership number"],
    };
    return map[service] || [];
  };

  const addressOptions = AddSuggData?.map((a) => ({
    value: a?.address,
    label: a?.address,
    addressId: a?.id,
  })) || [];

  return (
    <Layout>
      <Space style={{ marginBottom: 24 }} align="center">
        <Card size="small" style={{ textAlign: "center", width: 100 }} bodyStyle={{ padding: 8 }}>
          <img src={serviceIcon} alt={serviceName} style={{ width: 40, height: 40, objectFit: "contain" }} />
          <Text strong style={{ display: "block", fontSize: 11 }}>{serviceName}</Text>
        </Card>
        <Title level={4} style={{ margin: 0 }}>
          {data ? "Edit Company" : "Add Company"}
        </Title>
      </Space>

      <Card style={{ maxWidth: 600 }}>
        <Form form={form} layout="vertical" onFinish={onFinish} size="large">
          <Form.Item name="name" label="Company Name" rules={[{ required: true }]}>
            <Input placeholder="Enter company name" />
          </Form.Item>

          <Divider orientation="left" plain>Address Lookup</Divider>

          <AutoComplete
            style={{ width: "100%", marginBottom: 16 }}
            options={addressOptions}
            value={searchQuery}
            onSearch={(text) => { setEnteredText(text); setSearchQuery(text); }}
            onSelect={onAddressSelect}
            placeholder="Search by address or postcode"
          />

          <Divider orientation="left" plain>Address Details</Divider>

          <Form.Item name="addressline1" label="Address Line 1">
            <Input placeholder="Address line 1" />
          </Form.Item>
          <Form.Item name="addressline2" label="Address Line 2">
            <Input placeholder="Address line 2" />
          </Form.Item>
          <Form.Item name="town" label="Town/City">
            <Input placeholder="Town/City" />
          </Form.Item>
          <Form.Item name="county" label="County">
            <Input placeholder="County" />
          </Form.Item>
          <Form.Item name="postCode" label="Postcode">
            <Input placeholder="Postcode" />
          </Form.Item>
          <Form.Item name="longitude" label="Longitude">
            <Input placeholder="Longitude" />
          </Form.Item>
          <Form.Item name="latitude" label="Latitude">
            <Input placeholder="Latitude" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              {data ? "Update" : "Submit"}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </Layout>
  );
};

export default AddCompany;
